import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { offersAPI } from '../../lib/api';
import { toast } from 'sonner';
import { fetchWithCache } from '../../lib/cache';
import { Plus, Pencil, Trash2, Percent, RefreshCcw } from 'lucide-react';
import { usePopup } from '../../contexts/PopupContext';

export default function AdminOffers() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const { showPopup } = usePopup();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    discount_type: 'percentage',
    discount_value: '',
    min_order_value: '0',
    max_discount: '',
    coupon_code: '',
    is_active: true,
  });

  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    try {
      await fetchWithCache(
        'admin_offers_all',
        async () => {
          const response = await offersAPI.getAll();
          return response.data || [];
        },
        (data, isFromCache) => {
          setOffers(data);
          if (isFromCache) setLoading(false);
        }
      );
    } catch (error) {
      console.error('Failed to fetch offers:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      discount_type: 'percentage',
      discount_value: '',
      min_order_value: '0',
      max_discount: '',
      coupon_code: '',
      is_active: true,
    });
    setEditingOffer(null);
  };

  const handleEdit = (offer) => {
    setEditingOffer(offer);
    setFormData({
      title: offer.title,
      description: offer.description || '',
      discount_type: offer.discount_type,
      discount_value: String(offer.discount_value),
      min_order_value: String(offer.min_order_value || 0),
      max_discount: offer.max_discount !== null && offer.max_discount !== undefined ? String(offer.max_discount) : '',
      coupon_code: offer.coupon_code || '',
      is_active: offer.is_active,
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      discount_value: parseFloat(formData.discount_value),
      min_order_value: parseFloat(formData.min_order_value),
      max_discount: formData.max_discount !== '' && formData.max_discount !== null ? parseFloat(formData.max_discount) : null,
    };

    try {
      if (editingOffer) {
        await offersAPI.update(editingOffer.id, payload);
        toast.success('Offer updated');
      } else {
        await offersAPI.create(payload);
        toast.success('Offer created');
      }
      setShowDialog(false);
      resetForm();
      fetchOffers();
    } catch (error) {
      toast.error('Failed to save offer');
    }
  };

  const handleDelete = async (id) => {
    showPopup({
      title: "Delete Offer",
      message: "Are you sure you want to delete this offer?",
      type: "error",
      onConfirm: async () => {
        try {
          await offersAPI.delete(id);
          toast.success('Offer deleted');
          fetchOffers();
        } catch (error) {
          toast.error('Failed to delete offer');
        }
      }
    });
  };

  return (
    <div className="space-y-6" data-testid="admin-offers">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Offers & Coupons</h1>
          <p className="text-slate-400">Manage discounts and promotional offers</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              setRefreshing(true);
              await fetchOffers();
              setRefreshing(false);
            }}
            className="btn-admin border-slate-600 hover:bg-slate-700"
          >
            <RefreshCcw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="btn-admin bg-primary hover:bg-primary/90" data-testid="add-offer-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Offer
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-800 border-slate-700">
              <DialogHeader>
                <DialogTitle>{editingOffer ? 'Edit Offer' : 'Create New Offer'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Summer Sale 20% Off"
                    className="input-admin"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Offer description"
                    className="input-admin"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Discount Type</Label>
                    <Select value={formData.discount_type} onValueChange={(v) => setFormData({ ...formData, discount_type: v })}>
                      <SelectTrigger className="input-admin">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Percentage (%)</SelectItem>
                        <SelectItem value="flat">Flat (₹)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Discount Value *</Label>
                    <Input
                      type="number"
                      value={formData.discount_value}
                      onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                      placeholder="e.g., 20"
                      className="input-admin"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Min Order Value (₹)</Label>
                    <Input
                      type="number"
                      value={formData.min_order_value}
                      onChange={(e) => setFormData({ ...formData, min_order_value: e.target.value })}
                      placeholder="0"
                      className="input-admin"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Max Discount (₹)</Label>
                    <Input
                      type="number"
                      value={formData.max_discount}
                      onChange={(e) => setFormData({ ...formData, max_discount: e.target.value })}
                      placeholder="Optional"
                      className="input-admin"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Coupon Code (Optional)</Label>
                  <Input
                    value={formData.coupon_code}
                    onChange={(e) => setFormData({ ...formData, coupon_code: e.target.value.toUpperCase() })}
                    placeholder="e.g., SUMMER20"
                    className="input-admin"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                  <Label>Active</Label>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="ghost" onClick={() => setShowDialog(false)}>Cancel</Button>
                  <Button type="submit" className="bg-primary hover:bg-primary/90">
                    {editingOffer ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-700 hover:bg-transparent">
                <TableHead className="text-slate-400">Offer</TableHead>
                <TableHead className="text-slate-400">Discount</TableHead>
                <TableHead className="text-slate-400">Coupon Code</TableHead>
                <TableHead className="text-slate-400">Min Order</TableHead>
                <TableHead className="text-slate-400">Status</TableHead>
                <TableHead className="text-slate-400 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">Loading...</TableCell>
                </TableRow>
              ) : offers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                    <Percent className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    No offers found
                  </TableCell>
                </TableRow>
              ) : (
                offers.map((offer) => (
                  <TableRow key={offer.id} className="border-slate-700">
                    <TableCell>
                      <div>
                        <p className="font-medium">{offer.title}</p>
                        <p className="text-sm text-slate-400 truncate max-w-[200px]">{offer.description}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-amber-500/20 text-amber-400">
                        {offer.discount_type === 'percentage' ? `${offer.discount_value}%` : `₹${offer.discount_value}`}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono">{offer.coupon_code || '-'}</TableCell>
                    <TableCell>₹{offer.min_order_value}</TableCell>
                    <TableCell>
                      <Badge className={offer.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'}>
                        {offer.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button size="icon" variant="ghost" onClick={() => handleEdit(offer)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button size="icon" variant="ghost" className="text-red-400" onClick={() => handleDelete(offer.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
